import { useCallback, useState } from 'react';

import { faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { ICar, ISessionKey } from '@share/common-types';
import { carsActions, marginatorActions } from '@share/store/slices';
import { FunctionalRootState } from '@share/utils';
import { NULL_VALUE } from '@constants';
import { formatDateCheckInOut, renderNumber, LoginType } from '@share/utils';
import { PeopleSvg, SuitecaseSvg, TransmitionSvg, AirSvg } from '@assets';
import './style.scss';

import { DATE_FORMAT_CHECK_IN_OUT } from '@constants';
import { get } from 'lodash';
import { DownLoadShareType, HandleDownload } from '@share/api';
import { Modal, Spin, Tooltip } from 'antd';
import { BlueButton } from '@share/components';
import { Currency } from '@components';
import { HasPermission, ViewQuotesSharePermission, openCarPage } from '@utils';
import { ModalShare } from '../../../common';
import { getCarCompareRoutePath } from '@share/utils';

const modalWidth = 900;
const percents = 100;

export interface IcarPayload extends ICar {
  sessionKey: ISessionKey | null | undefined;
  carRequest: object;
}

export interface ModalCompareProps {
  visible: boolean;
  checkIn: string;
  checkOut: string;
  locationCode: string;
  sessionKey: ISessionKey | null | undefined;
  cars: IcarPayload[];
  onCancel: () => void;
  onRemove: (car: ICar) => void;
}

export function ModalCompare({
  visible,
  checkIn,
  checkOut,
  sessionKey,
  cars,
  onCancel,
  onRemove,
}: ModalCompareProps) {
  const intl = useIntl();
  const history = useHistory();
  const dispatch = useDispatch();

  const marginatorStore = useSelector((state: FunctionalRootState) => state.marginatorStore);
  const { account, user } = useSelector((state: FunctionalRootState) => state.loginStore);

  const [loading, setLoading] = useState(false);
  const [visibleAction, setVisibleAction] = useState(false);
  const [isShare, setIsShare] = useState(false);

  const handleView = (car: IcarPayload, marginatorCommission: number) => {
    dispatch(marginatorActions.setCommission(marginatorCommission));
    dispatch(carsActions.setSelectedCar(car));
    openCarPage(car.id, car.sessionKey ?? undefined, history, !!account?.disableNewTabOpen, !!account?.forceIframe);
  };

  const handleData = useCallback(
    (members?: string[], comment?: string) => {
      const { value } = marginatorStore;
      const isB2C = account?.isB2C;

      const data = {
        quotes: cars.map((car) => {
          const marginatorPrice = car.price / (1 - value / 100);
          const publicPrice =
            isB2C || !car.publicPrices?.length ? null : Math.min(...car.publicPrices.map((p) => p.price));
          const CarRoutePath = getCarCompareRoutePath(car.id, car.sessionKey);
          const CarfullPath = `{0}{1}${CarRoutePath}`;
          const IsRefundable = car.refundable;
          return {
            carId: car.id,
            displayName: car.vehicle?.vehMakeModel?.name,
            Category: car.vehicle?.category || NULL_VALUE,
            currency: car.currency,
            price: marginatorPrice,
            RentalCompany: car.vendor?.value || NULL_VALUE,
            RentalCompanyLogo: car?.info?.tpA_Extensions?.supplierLogo?.url || NULL_VALUE,
            publicPrice,
            IsRefundable,
            image: car.vehicle?.pictureURL,
            features: {
              seats: Number(car.vehicle?.passengerQuantity) || null,
              luggage: Number(car.vehicle?.baggageQuantity) || null,
              doors: Number(car.vehicle?.vehType?.doorCount) || null,
              transmission: car.vehicle?.transmissionType,
              airConditioning: car.vehicle?.airConditionInd,
              unlimitedMileage: true,
            },
            carRequest: car.carRequest,
            pickUpDate: checkIn,
            dropOffDate: checkOut,
            pickUpLocation: car.pickUpLocationDetail?.name,
            dropOffLocation: car.dropOffLocationDetail?.name,
            driverAge: 22,
            Link: CarfullPath,
          };
        }),
        comment,
        members,
        sessionKey,
      } as Record<string, unknown>;
      return data;
    },
    [cars, marginatorStore, account?.isB2C, checkIn, checkOut, sessionKey],
  );

  const handleDownload = () => {
    const prevLoading = loading;
    setLoading(true);
    HandleDownload(DownLoadShareType.CAR, prevLoading, handleData(), intl, () => setLoading(false), () =>
      setVisibleAction(false),
    );
  };

  const isB2C = account?.isB2C;

  return (
    <Modal
      className="modal-compare"
      open={visible}
      footer={null}
      onCancel={onCancel}
      wrapClassName="modal-compare__wrapper"
      width={modalWidth}
    >
      <div>
        <div className="modal-compare__title-row">
          <h4 className="modal-compare__title secondary-font">
            <FormattedMessage id="results.cars.card.compare.modal.title" />
          </h4>
        </div>

        <div className="modal-compare__hotels modal-compare__hotels--meta">
          <div className="hotels-dates">
            {formatDateCheckInOut(checkIn, DATE_FORMAT_CHECK_IN_OUT)} -{' '}
            {formatDateCheckInOut(checkOut, DATE_FORMAT_CHECK_IN_OUT)}
          </div>
        </div>

        <div className="modal-compare__hotels">
          {cars.map((car, index) => {
            const { price } = car;
            const { value } = marginatorStore;
            const marginatorPrice = price / (1 - value / 100);
            const marginatorCommission = marginatorPrice - price;

            const publicPrice =
              isB2C || !car.publicPrices?.length ? null : Math.min(...car.publicPrices.map((p) => p.price));

            const msave = publicPrice ? (marginatorPrice * percents) / publicPrice : 100;
            const savings = Math.floor(percents - msave);
            const logoUrl = car?.info?.tpA_Extensions?.supplierLogo?.url || '';

            return (
              <div key={index} className="modal-compare__hotel">
                <div className="modal-compare__media">
                  <div className="hotel-images-container hotelImageHeight">
                    <div
                      className="hotel-image"
                      style={{
                        backgroundImage: `url(${car.vehicle?.pictureURL || get(car, 'images[0].url')})`,
                      }}
                    />
                    {savings > 0 ? (
                      <div className="modal-compare__savings-badge">
                        {savings.toFixed(0)} % <FormattedMessage id="results.cars.card.compare.modal.off" />
                      </div>
                    ) : null}
                  </div>
                  <label
                    className={`modal-compare__remove-btn ${cars.length === 2 ? 'disabled' : ''}`}
                    onClick={() => {
                      if (cars.length > 2) {
                        onRemove(car);
                      }
                    }}
                  >
                    <FontAwesomeIcon icon={faCircleXmark} className="modal-compare__remove-btn-icon" />
                  </label>
                </div>

                <div className="hotel-text-container" style={{ minHeight: '20px', height: 'auto' }}>
                  <p
                    className="mb-0"
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      lineClamp: 1,
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {logoUrl ? (
                      <div className="cars-content-location__vendor">
                        <img alt="Vendor logo" src={logoUrl} style={{ maxWidth: '100%', height: 'auto' }} />
                      </div>
                    ) : (
                      <span>{car?.vehicle?.vehMakeModel?.name || '-'}</span>
                    )}
                  </p>
                </div>
                <div className="hotel-text-container" style={{ minHeight: '20px', height: 'auto' }}>
                  <p
                    className="mb-0"
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      lineClamp: 1,
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    <Tooltip title={car.vehicle?.vehMakeModel?.name} placement="top">
                      {car.vehicle?.vehMakeModel?.name}
                    </Tooltip>
                  </p>
                </div>

                <div className="hotel-text-container hotel-text-container--car-location">
                  <Tooltip
                    title={`${car.vendor?.value} ${car.pickUpLocationDetail?.name}`}
                    placement="top"
                  >
                    <p>
                      {car.vendor?.value} {car.pickUpLocationDetail?.name}
                    </p>
                  </Tooltip>
                </div>

                <div className="hotel-text-container" style={{ minHeight: '50px', height: 'auto' }}>
                  <div className="cars-card__item">
                    <TransmitionSvg />
                    <label className="ms-1">{car.vehicle?.transmissionType}</label>
                  </div>
                  <div className="cars-card__item">
                    <PeopleSvg />
                    <label className="ms-1">
                      <FormattedMessage id="cars.filter.seats" />: {car.vehicle?.passengerQuantity}
                    </label>
                  </div>
                  <div className="cars-card__item">
                    <SuitecaseSvg />
                    <label className="ms-1">
                      <FormattedMessage id="adult" values={{ count: car.vehicle?.passengerQuantity }} />:{' '}
                      {car.vehicle?.passengerQuantity}
                    </label>
                  </div>
                  <div className="cars-card__item">
                    <SuitecaseSvg />
                    <label className="ms-1">
                      <FormattedMessage id="cars.baggage" />: {car.vehicle?.baggageQuantity}
                    </label>
                  </div>
                  <div className="cars-card__item">
                    <AirSvg />
                    <label className="ms-1">
                      <FormattedMessage id="cars.ac" />
                    </label>
                  </div>
                </div>

                <div className="hotel-text-container hotel-text-container--refundable">
                  <div className="hotel-card-wrapper__refundable">
                    {car.refundable ? (
                      <div className="hotel-card-wrapper__refundable-ref">
                        <FormattedMessage id="refundable" />
                      </div>
                    ) : (
                      <div className="hotel-card-wrapper__refundable-nonref">
                        <FormattedMessage id="non.refundable" />
                      </div>
                    )}
                  </div>
                </div>
                <div className="hotel-text-container hotel-text-container--price">
                  {publicPrice ? (
                    <p className="mb-0" style={{ textDecoration: 'line-through' }}>
                      <Currency currency={car.currency} />
                      {renderNumber(publicPrice, 2)}
                    </p>
                  ) : null}
                  <p className="mb-0">
                    <strong>
                      <Currency currency={car.currency} />
                      {renderNumber(marginatorPrice, 2)}
                    </strong>
                  </p>
                </div>

                <div className="hotel-view-container">
                  <div className="view-button" onClick={() => handleView(car, marginatorCommission)}>
                    <FormattedMessage id="results.cars.card.compare.modal.view" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="modal-compare__buttons">
          {!account || account.type !== LoginType.PrivateWithToken ? (
            account?.displayShareCompareCars && (
              <BlueButton disabled={loading} onClick={handleDownload}>
                {loading ? <Spin /> : <FormattedMessage id="results.cars.card.compare.modal.download" />}
              </BlueButton>
            )
          ) : (
            <>
              {account?.displayShareCompareCars && (
                <BlueButton
                  disabled={loading}
                  onClick={() => {
                    setVisibleAction(true);
                    setIsShare(false);
                  }}
                >
                  {loading ? <Spin /> : <FormattedMessage id="results.cars.card.compare.modal.download" />}
                </BlueButton>
              )}

              {HasPermission(user, ViewQuotesSharePermission) ? (
                <BlueButton
                  disabled={loading}
                  onClick={() => {
                    setVisibleAction(true);
                    setIsShare(true);
                  }}
                >
                  {loading ? <Spin /> : <FormattedMessage id="results.cars.card.compare.modal.share" />}
                </BlueButton>
              ) : null}
            </>
          )}
        </div>
      </div>

      <ModalShare
        visible={visibleAction}
        isShare={isShare}
        type={DownLoadShareType.CAR}
        onCancel={() => setVisibleAction(false)}
        onLoading={(v) => setLoading(v)}
        convertData={handleData}
      />
    </Modal>
  );
}
